import { z } from 'zod';
import { GoalDirection, GoalMetricKind, GoalPeriodKind } from './performance-goal.js';
/**
 * Thư viện OKR mẫu — APM-OKR4-b, [docs/plan-okr-knowledge.md](../../../docs/plan-okr-knowledge.md) §4.
 *
 * ## Vì sao nó **không** nằm trong sổ kiến thức
 *
 * Sổ kiến thức (`org_knowledge`) là lớp 2: vào thẳng system prompt, trần 3 000
 * token, và đúng ở **mọi** lượt. Thư viện này ngược lại — vài chục mẫu, mỗi
 * lượt chỉ vài mẫu liên quan. Nhét cả thư viện vào lớp 2 thì `fitKnowledge` cắt
 * im lặng, và mỗi lần sửa một mẫu là mất cache của mọi lượt cùng tag.
 *
 * Nên nó sống ở **lớp 6**: model tự gọi `search_okr_library` khi cần. Đúng luật
 * plan-harness §6.3 — *"có thể lấy qua tool thì không nhồi sẵn"*.
 */
/**
 * Cam kết hay khát vọng — Google OKR Playbook.
 *
 * ⚠️ Hai loại chấm bằng **hai thước**: cam kết kỳ vọng đạt đủ, khát vọng kỳ
 * vọng ~70% và đạt 0,6–0,7 *là* thành công. Gắn nhầm khát vọng thành cam kết
 * là tạo sẵn một thất bại trên giấy, và đó là cái bẫy Google nêu đích danh.
 */
export const OkrAmbition = z.enum(['committed', 'aspirational']);
/**
 * Vai trò mà mẫu hợp nhất. `any` = hợp với mọi vai.
 *
 * ⚠️ Cố ý **không** khớp với `PROJECT_ROLE_GRANTS`: đây là gợi ý cho model chọn
 * mẫu, không phải một cửa phân quyền. Buộc nó khớp danh sách vai trò thật sẽ
 * làm một thay đổi phân quyền kéo theo việc viết lại thư viện.
 */
export const OkrRoleHint = z.enum(['dev', 'qa', 'ba', 'design', 'ops', 'lead', 'any']);
/**
 * Một Key Result trong mẫu — **union**, đúng hình dạng của `CreatePersonalGoalInput`.
 *
 * Nhánh `metric` là chỉ số của danh mục (job đêm đo hộ). Nhánh `free` là mục
 * tiêu tự nhập số. Cả hai đều cần: một Objective định tính như "cải thiện danh
 * tiếng" không có chỉ số hệ thống nào, và nếu thư viện chỉ toàn mẫu gắn
 * `metricKind` thì model sẽ không bao giờ đề xuất OKR tự do.
 */
export const OkrTemplateKeyResult = z.union([
    z.object({
        kind: z.literal('metric'),
        metricKind: GoalMetricKind,
        /** Cách nói mức đích cho mẫu — chữ, không phải số: số thật do mốc đo quyết. */
        hint: z.string().min(1).max(200),
    }),
    z.object({
        kind: z.literal('free'),
        title: z.string().min(1).max(200),
        unit: z.string().min(1).max(30),
        direction: GoalDirection,
        /** Lấy số ở đâu — bắt buộc, vì không ai đo hộ mục tiêu tự do. */
        source: z.string().min(1).max(200),
    }),
]);
export const OkrTemplate = z.object({
    id: z.string().min(1).max(60),
    /** Câu định tính, **không có số** — số nằm ở key result. */
    objective: z.string().min(1).max(300),
    /** Vì sao Objective này đáng đặt. Vào kết quả tool để model trích được. */
    why: z.string().min(1).max(600),
    roleHint: OkrRoleHint,
    ambition: OkrAmbition,
    periodKind: GoalPeriodKind,
    keyResults: z.array(OkrTemplateKeyResult).min(1).max(4),
    source: z.string().max(200).nullable(),
});
//# sourceMappingURL=okr-template.js.map